import apiService from './api';
import { Viagem, ApiResponse, RespostaPaginada } from '../types';

export interface ViagensFilters {
  veiculo_id?: number;
  motorista_id?: number;
  status?: string;
  tipo?: string;
  data_inicio?: string;
  data_fim?: string;
  origem?: string;
  destino?: string;
  /** Um termo so, procurado na origem, nas paradas e no destino. */
  busca?: string;
  sort_by?: string;
  sort_order?: string;
  per_page?: number;
  page?: number;
}

export interface ViagemResponse {
  success: boolean;
  data: Viagem;
  message?: string;
}

export interface ViagensEstatisticas {
  total_viagens: number;
  viagens_planejadas: number;
  viagens_em_andamento: number;
  viagens_concluidas: number;
  viagens_canceladas: number;
  km_total_percorridos: number;
  valor_total_gasto: number;
  tempo_total_viagem: number;
  viagens_por_tipo: Record<string, number>;
  viagens_por_status: Record<string, number>;
}

/** Um lugar sugerido pelo mapa. O `place_id` e a identidade dele. */
export interface SugestaoDeLugar {
  place_id: string;
  descricao: string;
}

/** Um item conferido, ja com o nome por extenso resolvido pelo servidor. */
export interface ItemConferido {
  chave: string;
  rotulo: string;
  ok: boolean;
  observacao: string | null;
}

export interface ChecklistDaViagem {
  id: number;
  created_at: string;
  observacoes: string | null;
  motorista: string | null;
  itens: ItemConferido[];
  /** Recorte de `itens`: e por onde o gestor comeca a ler. */
  reprovados: ItemConferido[];
  fotos: Array<{ caminho: string; url: string }>;
}

export const viagensService = {
  // Listar viagens com filtros
  getViagens: async (filters: ViagensFilters = {}): Promise<RespostaPaginada<Viagem>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<RespostaPaginada<Viagem>>(`/v1/viagens?${params.toString()}`);
    return response;
  },

  // Buscar viagem por ID
  getViagem: async (id: number): Promise<ViagemResponse> => {
    const response = await apiService.get<ViagemResponse>(`/v1/viagens/${id}`);
    return response;
  },

  // Criar nova viagem
  createViagem: async (data: Partial<Viagem>): Promise<ViagemResponse> => {
    const response = await apiService.post<ViagemResponse>('/v1/viagens', data);
    return response;
  },

  // Atualizar viagem
  updateViagem: async (id: number, data: Partial<Viagem>): Promise<ViagemResponse> => {
    const response = await apiService.put<ViagemResponse>(`/v1/viagens/${id}`, data);
    return response;
  },

  // Excluir viagem
  deleteViagem: async (id: number): Promise<ApiResponse<any>> => {
    const response = await apiService.delete<ApiResponse<any>>(`/v1/viagens/${id}`);
    return response;
  },

  // Buscar viagens por veículo
  getByVeiculo: async (veiculoId: number): Promise<ApiResponse<Viagem[]>> => {
    const response = await apiService.get<ApiResponse<Viagem[]>>(`/v1/viagens/veiculo/${veiculoId}`);
    return response;
  },

  // Buscar viagens por motorista
  getByMotorista: async (motoristaId: number): Promise<ApiResponse<Viagem[]>> => {
    const response = await apiService.get<ApiResponse<Viagem[]>>(`/v1/viagens/motorista/${motoristaId}`);
    return response;
  },

  // Estatísticas de viagens
  getEstatisticas: async (filters: Partial<ViagensFilters> = {}): Promise<ApiResponse<ViagensEstatisticas>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<ViagensEstatisticas>>(`/v1/viagens/estatisticas?${params.toString()}`);
    return response;
  },

  // Iniciar viagem
  iniciarViagem: async (id: number): Promise<ViagemResponse> => {
    const response = await apiService.post<ViagemResponse>(`/v1/viagens/${id}/iniciar`);
    return response;
  },

  // Finalizar viagem
  finalizarViagem: async (id: number, data: { km_chegada: number; data_chegada?: string }): Promise<ViagemResponse> => {
    const response = await apiService.post<ViagemResponse>(`/v1/viagens/${id}/finalizar`, data);
    return response;
  },

  /**
   * Sugestoes de endereco enquanto o gestor digita.
   *
   * Passa pelo backend, e nao direto no Google: a chave e do servidor, e assim
   * o mesmo trecho digitado por dez pessoas custa uma consulta so.
   */
  buscarLugares: async (termo: string): Promise<ApiResponse<SugestaoDeLugar[]> & { configurado: boolean }> => {
    return apiService.post('/v1/viagens/lugares', { termo });
  },

  /**
   * A distancia da rota, para a tela mostrar antes de salvar.
   *
   * O valor que fica gravado e recalculado no servidor ao criar a viagem: este
   * aqui e so o que o gestor ve. Nao ha desperdicio — a segunda chamada acerta
   * o cache.
   */
  calcularDistancia: async (
    origemPlaceId: string,
    destinoPlaceId: string,
    paradasPlaceIds: string[] = [],
  ): Promise<ApiResponse<{ distancia_km: number | null; configurado: boolean }>> => {
    return apiService.post('/v1/viagens/distancia', {
      origem_place_id: origemPlaceId,
      destino_place_id: destinoPlaceId,
      paradas_place_ids: paradasPlaceIds,
    });
  },

  /**
   * O checklist de saida que o motorista preencheu para a viagem.
   *
   * 404 quando nao existe, e isso e informacao: a viagem ainda nao foi
   * conferida, ou e antiga demais para ter passado pela regra.
   */
  getChecklist: async (id: number): Promise<ApiResponse<ChecklistDaViagem>> => {
    return apiService.get<ApiResponse<ChecklistDaViagem>>(`/v1/viagens/${id}/checklist`);
  },

  // Cancelar viagem
  cancelarViagem: async (id: number): Promise<ViagemResponse> => {
    const response = await apiService.post<ViagemResponse>(`/v1/viagens/${id}/cancelar`);
    return response;
  }
};
